'use client';

import Link from 'next/link';
import Image from 'next/image';
import { 
  Scale, MapPin, BarChart3, Users, ArrowRight, ChevronDown 
} from 'lucide-react';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';

export interface StoneInvestmentValuationsSectionProps {
  messages?: Record<string, unknown>;
  locale?: string;
}

// Helper function to get nested translation value
const getNestedValue = (obj: Record<string, unknown>, path: string, fallback: string): string => {
  const keys = path.split('.');
  let value: unknown = obj;
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = (value as Record<string, unknown>)[key];
    } else {
      return fallback;
    }
  }
  return typeof value === 'string' ? value : fallback;
};

// Refined Methodology Card - Grid Style
const MethodologyCard = ({ 
  icon: Icon, 
  title, 
  description, 
  index 
}: { 
  icon: React.ElementType; 
  title: string; 
  description: string; 
  index: number;
}) => (
  <div className="group relative p-8 md:p-12 border-l border-b border-stone-200 hover:bg-stone-100/50 transition-colors duration-500">
    <span className="absolute top-8 right-8 text-xs font-sans font-bold text-stone-200 group-hover:text-stone-300 transition-colors">0{index}</span>
    <div className="mb-8 text-stone-400 group-hover:text-stone-900 transition-colors duration-500">
      <Icon size={32} strokeWidth={1} />
    </div>
    <h3 className="font-serif text-2xl text-stone-900 mb-4">{title}</h3>
    <p className="font-sans font-light text-sm text-stone-500 leading-relaxed max-w-sm">
      {description}
    </p>
  </div>
);

// Refined Exclusivity List Item
const ExclusivityItem = ({ text }: { text: string }) => (
  <li className="flex items-start gap-4 group py-4 border-b border-stone-700/50 last:border-0">
    <div className="mt-2 w-1.5 h-1.5 rounded-full bg-stone-400 group-hover:bg-provence-accent transition-colors duration-300 shrink-0 shadow-sm" />
    <span className="font-sans text-sm text-stone-300 group-hover:text-white transition-colors leading-relaxed tracking-wide">
      {text}
    </span>
  </li>
);

const ExclusivityColumn = ({ 
  title, 
  subtitle, 
  items 
}: { 
  title: string; 
  subtitle: string; 
  items: string[];
}) => (
  <div className="h-full bg-stone-800 p-8 md:p-10 border border-stone-700/50 hover:border-stone-500 transition-colors duration-500 flex flex-col shadow-2xl">
    <div className="mb-8">
      <p className="text-[10px] uppercase tracking-widest text-stone-400 mb-2 font-medium font-sans">{subtitle}</p>
      <h4 className="font-serif text-3xl text-white">{title}</h4>
    </div>
    <ul className="flex-1 space-y-1">
      {items.map((item, idx) => (
        <ExclusivityItem key={idx} text={item} />
      ))}
    </ul>
  </div>
);

/**
 * Stone Investment - Valuations Section
 * 
 * Concept One Design (Exact Match):
 * - Hero with "Precision & Value"
 * - Philosophy section with sticky content
 * - Methodology grid (4 cards)
 * - Exclusivity columns (3 columns)
 * - CTA section with decorative text
 */
export function StoneInvestmentValuationsSection({ 
  locale = 'en',
  messages
}: StoneInvestmentValuationsSectionProps) {
  // Get stone-specific translations
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const valuationsMessages = (stoneMessages.valuations || {}) as Record<string, unknown>;
  
  const t = (path: string, fallback: string): string => {
    return getNestedValue(valuationsMessages, path, fallback);
  };
  
  // Get exclusivity items arrays
  const exclusivityMessages = (valuationsMessages.exclusivity || {}) as Record<string, unknown>;
  const commitmentItems = ((exclusivityMessages.commitment as Record<string, unknown>)?.items as string[]) || [
    '6-month renewable exclusive duration',
    'Professional valuation & strategy',
    'Geolocation for qualified visits',
    'Home-staging & decoration advice',
    'Professional photo and video coverage',
    'Priority newsletter distribution'
  ];
  const confidenceItems = ((exclusivityMessages.confidence as Record<string, unknown>)?.items as string[]) || [
    'Assurance of market-correct pricing',
    'Trust in the agency\'s obligation of result',
    'Access to complete, transparent data',
    'High-quality, targeted visits',
    'Time to secure proper financing',
    'Direct relationship with decision makers'
  ];
  const resultsItems = ((exclusivityMessages.results as Record<string, unknown>)?.items as string[]) || [
    'Sold at a higher price than open mandates',
    'No downward competition between agencies',
    'Offers often made without loan conditions',
    'Sale within 3 months on average',
    'Streamlined negotiation process',
    'Total peace of mind'
  ];
  
  return (
    <div className="w-full bg-stone-50 min-h-screen pb-24">
      
      {/* --- ACT 1: HERO (REFINED) --- */}
      <header className="relative w-full h-[85vh] overflow-hidden bg-stone-900">
        <div className="absolute inset-0 scale-105 animate-pulse" style={{ animationDuration: '20s' }}>
          {/* Abstract Architectural Image */}
          <Image 
            src="https://media.routeroyal.com/Vha_D7-NX7vhtg6VwKvy5Xf-D04atJoOfpFQCRsPIhY/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvMmJhNTZmM2ViZjY1N2EwMzU2N2JjNGQ1OWRlYzVkMmE0MTZiNWJjMmMxN2I5MTAzOGJiNTg3MTZjNzMwZmFmOC5qcGc.jpg"
            alt="Luxury property valuation"
            fill
            className="object-cover opacity-50"
            priority
          />
          <div className="absolute inset-0 bg-linear-to-b from-stone-900/60 via-stone-900/20 to-stone-50" />
        </div>
        
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <div className="space-y-8 max-w-4xl">
            <p className="font-sans text-xs uppercase tracking-[0.4em] text-white/80 opacity-0 animate-[fadeUp_1s_ease-out_0s_forwards]">
              {t('hero.badge', 'Strategic Advisory')}
            </p>
            <h1 className="font-serif text-6xl md:text-8xl lg:text-9xl text-stone-100 leading-none opacity-0 animate-[fadeUp_1s_ease-out_0.1s_forwards]">
              {t('hero.title', 'Precision & Value')}
            </h1>
            <p className="font-sans font-light text-lg md:text-xl text-stone-300 max-w-2xl mx-auto leading-relaxed opacity-0 animate-[fadeUp_1s_ease-out_0.2s_forwards]">
              {t('hero.subtitle', 'Unlocking the true potential of your asset through rigorous analysis and market intelligence.')}
            </p>
          </div>
        </div>

        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce opacity-50">
          <ChevronDown className="text-stone-400" size={24} />
        </div>
      </header>

      {/* --- ACT 2: THE PHILOSOPHY (REFINED) --- */}
      <section className="container mx-auto px-6 md:px-24 py-32 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row gap-16 items-start">
            <div className="flex-1 sticky top-32">
              <span className="block w-12 h-1 bg-stone-900 mb-8" />
              <h2 className="font-serif text-4xl md:text-5xl text-stone-900 leading-tight mb-8">
                {t('philosophy.title', 'The Science of')} <br/><span className="italic text-stone-500">{t('philosophy.titleAccent', 'Evaluation')}</span>
              </h2>
            </div>
            <div className="flex-1 space-y-8">
              <p className="font-sans font-light text-stone-600 text-lg leading-relaxed">
                {t('philosophy.paragraph1', 'A precise valuation acts as the cornerstone of a successful transaction. It bridges the gap between market reality and the unique narrative of your property.')}
              </p>
              <p className="font-sans font-light text-stone-600 text-lg leading-relaxed">
                {t('philosophy.paragraph2', 'Our approach is data-driven yet human-centric. We verify every estimate against realized sales, ensuring that when we speak of value, we speak of certainty.')}
              </p>
              <div className="bg-stone-100 p-8 border-l-2 border-stone-300 italic font-serif text-xl text-stone-500">
                {t('philosophy.quote', '"The majority of our transactions align perfectly with our initial estimates. This precision is why our clients trust us with their legacy."')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- ACT 3: THE CRITERIA (REFINED GRID) --- */}
      <section className="border-t border-stone-200 bg-white">
        <div className="container mx-auto px-6 md:px-24 py-32">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="text-stone-400 uppercase tracking-[0.3em] text-xs font-sans">{t('methodology.badge', 'Methodology')}</span>
            <h3 className="mt-4 font-serif text-4xl text-stone-900">{t('methodology.title', 'The Anatomy of Value')}</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-r border-stone-200 max-w-6xl mx-auto">
            <MethodologyCard 
              index={1}
              icon={MapPin}
              title={t('methodology.context.title', 'Context & Location')}
              description={t('methodology.context.description', 'We analyze the micro-market, neighborhood dynamics, and future urban developments that impact long-term value.')}
            />
            <MethodologyCard 
              index={2}
              icon={Scale}
              title={t('methodology.character.title', 'Intrinsic Character')}
              description={t('methodology.character.description', 'A detailed assessment of volumes, light, materials, and architectural integrity. We weigh strengths against weaknesses with objectivity.')}
            />
            <MethodologyCard 
              index={3}
              icon={BarChart3}
              title={t('methodology.market.title', 'Market Intelligence')}
              description={t('methodology.market.description', "Utilizing high-performance tools to analyze registered sales and current 'buyer' market conditions at the specific moment of sale.")}
            />
            <MethodologyCard 
              index={4}
              icon={Users}
              title={t('methodology.buyer.title', 'Buyer Psychology')}
              description={t('methodology.buyer.description', 'Understanding the motivations and financing capacity of active buyers. Following the fundamental rule: Who buys what?')}
            />
          </div>
        </div>
      </section>

      {/* --- ACT 4: EXCLUSIVITY (REFINED COLUMNS) --- */}
      <section className="bg-stone-900 text-stone-50 py-32 px-6 md:px-24">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-20 pb-8 border-b border-stone-800">
            <div className="max-w-2xl">
              <span className="text-stone-400 uppercase tracking-[0.3em] text-xs font-sans">{t('exclusivity.badge', 'Sole Agent Contract')}</span>
              <h3 className="mt-6 font-serif text-5xl md:text-6xl text-white">{t('exclusivity.title', 'The Power of Exclusivity')}</h3>
            </div>
            <p className="mt-8 md:mt-0 font-sans font-light text-stone-400 max-w-md text-sm leading-relaxed text-right">
              {t('exclusivity.subtitle', 'The benefits of an exclusive mandate are often overlooked, yet they are the catalyst for exceptional results. We owe you success.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ExclusivityColumn 
              title={t('exclusivity.commitment.title', 'Commitment')}
              subtitle={t('exclusivity.commitment.subtitle', 'Our Obligation')}
              items={commitmentItems}
            />
            <ExclusivityColumn 
              title={t('exclusivity.confidence.title', 'Confidence')}
              subtitle={t('exclusivity.confidence.subtitle', 'Buyer Perception')}
              items={confidenceItems}
            />
            <ExclusivityColumn 
              title={t('exclusivity.results.title', 'Results')}
              subtitle={t('exclusivity.results.subtitle', 'Proven Outcomes')}
              items={resultsItems}
            />
          </div>
        </div>
      </section>

      {/* --- ACT 5: CTA (REFINED) --- */}
      <section className="py-40 px-6 md:px-24 bg-stone-50 flex flex-col items-center justify-center text-center relative overflow-hidden">
        {/* Decorative Background Text */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-serif text-[20vw] text-stone-200/50 italic pointer-events-none select-none">
          {t('cta.decorative', 'Value')}
        </div>
        
        <div className="relative z-10 max-w-2xl space-y-8">
          <h2 className="font-serif text-5xl md:text-6xl text-stone-900">{t('cta.title', 'Begin the Conversation')}</h2>
          <p className="font-sans font-light text-stone-500 text-lg">
            {t('cta.subtitle', 'We invite you to contact us for a confidential assessment of your property.')}
          </p>
          <div className="pt-8">
            <Link 
              href={`/${locale}/contact?intent=valuation`}
              onClick={() => analyticsEvents.custom('cta_click', {
                cta_text: t('cta.button', 'Request Valuation'),
                cta_location: 'valuations',
                cta_destination: `/${locale}/contact?intent=valuation`,
                cta_variant: 'primary',
              })}
              className="group relative inline-flex items-center gap-4 px-12 py-5 overflow-hidden bg-stone-900 text-white shadow-2xl hover:bg-stone-800 transition-all duration-500"
            >
              <span className="relative z-10 text-xs uppercase tracking-widest group-hover:tracking-[0.2em] transition-all duration-500 font-sans">
                {t('cta.button', 'Request Valuation')}
              </span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
